import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Icons
import HomeIcon from "@mui/icons-material/Home";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import MapIcon from "@mui/icons-material/Map";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import GuestLoginModal from "../.../../../../../Components/GuestLoginModal";
import { AuthContext } from "../../../AuthContext/AuthContext";
import "../../Lease Module/CustomBottomNav.css";

/* ---------------- NORMAL NAV ---------------- */
const normalNavItems = [
  { key: "home", icon: <HomeIcon />, route: "/dashboard", label: "Home" },
  { key: "list", icon: <FormatListBulletedIcon />, route: "/details", label: "List" },
  { key: "saves", icon: <FavoriteBorderIcon />, route: "/rent-saves", label: "Saves" },
  { key: "inbox", icon: <MailOutlineIcon />, route: "/rent-inboxlist", label: "Inbox" },
];

// ✅ Rent Map tab — shown on /details (replaces List)
const RENT_MAP_ITEM = {
  key: "map",
  icon: <MapIcon />,
  route: "/rent-propertymap",
  label: "Map",
};

// ✅ Rent List tab — shown on /rent-propertymap (replaces List back)
const RENT_LIST_ITEM = {
  key: "list",
  icon: <FormatListBulletedIcon />,
  route: "/details",
  label: "List",
};

/* ---------------- SPECIAL NAV ---------------- */
const specialNavItems = [
  {
    key: "home",
    icon: <HomeIcon />,
    route: "/dashboard",
    label: "Home",
  },
  {
    key: "list",
    icon: <FormatListBulletedIcon />,
    route: ["/details", "/postbank_detail"],
    label: "List",
  },
  {
    key: "services",
    icon: <CleaningServicesIcon />,
    route: "/home-service",
    label: <span className="services-label">Home Services</span>,
  },
  {
    key: "profile",
    icon: <AccountCircleIcon />,
    route: "/work-detail",
    label: "More",
  },
];

// ✅ Map tab that replaces List inside the SPECIAL nav when on /Post_Bankcard_indetail
const POSTBANK_MAP_ITEM = {
  key: "postbank-map",
  icon: <MapIcon />,
  route: "/postbank-card",
  label: "Map",
};

const BottomNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);
  const [currentKey, setCurrentKey] = useState("");
  const { isGuest } = useContext(AuthContext);
  const [showGuestModal, setShowGuestModal] = useState(false);

  // Tabs restricted for guests
  const guestRestrictedTabs = ["saves", "inbox"];

  /* --------- Pages where special nav should appear --------- */
  const specialPages = ["/postbank-card", "/Post_Bankcard_indetail"];

  const isSpecialPage = specialPages.some((path) =>
    location.pathname.startsWith(path)
  );

  /* --------- ✅ Detect page variants --------- */
  const isPostBankIndetailPage = location.pathname.startsWith("/Post_Bankcard_indetail");
  const isPostBankCardPage = location.pathname.startsWith("/postbank-card");

  const isRentDetailsPage = location.pathname.startsWith("/details");
  const isRentMapPage = location.pathname.startsWith("/rent-propertymap");

  /* --------- ✅ BUILD SPECIAL NAV ---------
       /postbank-card            → List  (unchanged)
       /Post_Bankcard_indetail   → Map   (swap List → Map)
  ---------------------------------------- */
  const activeSpecialNavItems = isPostBankIndetailPage
    ? specialNavItems.map((item) =>
        item.key === "list" ? POSTBANK_MAP_ITEM : item
      )
    : specialNavItems;

  /* --------- ✅ BUILD NORMAL NAV ---------
       /details                → Map  (swap List → Map)
       /rent-propertymap       → List (swap List → List)
  ---------------------------------------- */
  const activeNormalNavItems = isRentDetailsPage
    ? normalNavItems.map((item) =>
        item.key === "list" ? RENT_MAP_ITEM : item
      )
    : isRentMapPage
      ? normalNavItems.map((item) =>
          item.key === "list" ? RENT_LIST_ITEM : item
        )
      : normalNavItems;

  /* --------- Active tab logic --------- */
  useEffect(() => {
    /* ── Special pages ── */
    if (isSpecialPage) {
      if (isPostBankIndetailPage) {
        setCurrentKey("postbank-map");
        return;
      }
      if (isPostBankCardPage) {
        setCurrentKey("list");
        return;
      }

      const active =
        activeSpecialNavItems.find((item) => {
          if (Array.isArray(item.route)) {
            return item.route.some((route) =>
              location.pathname.startsWith(route)
            );
          }
          return location.pathname.startsWith(item.route);
        })?.key || activeSpecialNavItems[0].key;

      setCurrentKey(active);
      return;
    }

    /* ── Normal pages ── */
    if (isRentMapPage) {
      setCurrentKey("list");
      return;
    }
    if (isRentDetailsPage) {
      setCurrentKey("map");
      return;
    }

    const active =
      activeNormalNavItems.find((item) => {
        if (Array.isArray(item.route)) {
          return item.route.some((route) =>
            location.pathname.startsWith(route)
          );
        }
        return location.pathname.startsWith(item.route);
      })?.key || activeNormalNavItems[0].key;

    setCurrentKey(active);
  }, [
    location.pathname,
    isSpecialPage,
    isRentDetailsPage,
    isRentMapPage,
    isPostBankIndetailPage,
    isPostBankCardPage,
  ]);

  /* --------- Indicator movement --------- */
  useEffect(() => {
    const current = navRef.current?.querySelector(
      `.nav-item[data-key="${currentKey}"]`
    );
    const indicator = navRef.current?.querySelector(".nav-indicator");

    if (current && indicator) {
      const { offsetLeft, offsetWidth } = current;
      indicator.style.left = `${offsetLeft + offsetWidth / 2 - 25}px`;
    }
  }, [
    currentKey,
    isSpecialPage,
    isRentDetailsPage,
    isRentMapPage,
    isPostBankIndetailPage,
    isPostBankCardPage,
  ]);

  const handleNav = (item) => {
    // 🚫 Block guest access
    if (isGuest && guestRestrictedTabs.includes(item.key)) {
      setShowGuestModal(true);
      return;
    }

    setCurrentKey(item.key);

    // ✅ Post Bank Map tab → back to /postbank-card
    if (item.key === "postbank-map") {
      navigate("/postbank-card");
      return;
    }

    // ✅ Rent Map tab → /rent-propertymap
    if (item.key === "map") {
      navigate("/rent-propertymap");
      return;
    }

    // ✅ Rent List tab → /details
    if (item.key === "list" && isRentMapPage) {
      navigate("/details");
      return;
    }

    // Handle array of routes
    if (Array.isArray(item.route)) {
      if (isSpecialPage) {
        navigate("/postbank_detail");
      } else {
        navigate("/details");
      }
    } else {
      navigate(item.route);
    }
  };

  return (
    <div className="nav-wrapper">
      <div className="nav-bar" ref={navRef}>
        {/* ---------- NORMAL NAV ---------- */}
        {!isSpecialPage &&
          activeNormalNavItems.map((item) => (
            <div
              key={item.key}
              data-key={item.key}
              className={`nav-item ${
                currentKey === item.key ? "nav-item-active" : ""
              }`}
              onClick={() => handleNav(item)}
            >
              {item.icon}
              <div className="nav-label">{item.label}</div>
            </div>
          ))}

        {/* ---------- SPECIAL NAV ---------- */}
        {isSpecialPage &&
          activeSpecialNavItems.map((item) => (
            <div
              key={item.key}
              data-key={item.key}
              className={`nav-item ${
                currentKey === item.key ? "nav-item-active" : ""
              }`}
              onClick={() => handleNav(item)}
            >
              {item.icon}
              <div className="nav-label">{item.label}</div>
            </div>
          ))}

        <div className="nav-indicator" />
      </div>

      <GuestLoginModal
        open={showGuestModal}
        onClose={() => setShowGuestModal(false)}
      />
    </div>
  );
};

export default BottomNavbar;